import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AI_OPERATION_TERMINAL_STATUSES, type AiOperationDto, type AiOperationStatus } from '@scoutaround/script-breakdown-shared';

import { api } from '../../lib/api.js';
import { refreshCredits } from './useProjectCredits.js';

/** How often a running operation is asked about. */
export const OPERATION_POLL_MS = 3_000;

/**
 * Forty minutes at one poll every three seconds. A feature-length reading is
 * minutes; an operation still open after this is waiting on settlement
 * (`SETTLEMENT_PENDING` can take up to a day), which the summary says rather
 * than polling for as long as the tab lives.
 */
export const MAX_OPERATION_POLLS = 800;

export function operationIsFinal(status: AiOperationStatus | undefined): boolean {
  return status !== undefined && AI_OPERATION_TERMINAL_STATUSES.includes(status);
}

const operationKey = (projectId: string, operationId: string | null) => ['ai-operation', projectId, operationId] as const;

/**
 * ONE AI OPERATION, UNTIL IT IS SETTLED (S2b's post-run summary).
 *
 * Polls `GET …/ai-operations/:id` while the operation is open — reserved,
 * running, settling or under reconciliation — and stops once it is SETTLED,
 * CANCELLED or EXPIRED, or after `MAX_OPERATION_POLLS`. The moment it reaches a
 * final state the wallet and the history are refreshed ONCE, because that is
 * when the charge and the release land in them.
 */
export function useAiOperation(projectId: string, operationId: string | null): {
  operation: AiOperationDto | null;
  final: boolean;
  /** Still open, and no longer being asked about. */
  givenUp: boolean;
  isPending: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: operationKey(projectId, operationId),
    queryFn: () => api.aiOperation(projectId, operationId as string),
    enabled: operationId !== null,
    refetchInterval: (q) => {
      if (operationIsFinal(q.state.data?.status)) return false;
      if (q.state.dataUpdateCount > MAX_OPERATION_POLLS) return false;
      // A failed read stops the poll; the summary offers "Check again".
      if (q.state.status === 'error') return false;

      return OPERATION_POLL_MS;
    },
  });

  const operation = query.data ?? null;
  const final = operationIsFinal(operation?.status);
  // The update count is on the cached query, not on the observer's result.
  const polls = queryClient.getQueryState(operationKey(projectId, operationId))?.dataUpdateCount ?? 0;

  const refreshed = useRef<string | null>(null);
  useEffect(() => {
    if (!final || operationId === null || refreshed.current === operationId) return;
    refreshed.current = operationId;
    void refreshCredits(queryClient, projectId);
  }, [final, operationId, projectId, queryClient]);

  return {
    operation,
    final,
    givenUp: !final && polls > MAX_OPERATION_POLLS,
    isPending: query.isPending,
    error: query.error,
    refetch: () => {
      void query.refetch();
    },
  };
}
